import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import ExportConfirmation from './ExportConfirmation';
import SearchableDropdown from './SearchableDropdown';
import { useLanguage } from '../LanguageContext';

const Icon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="7" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.2" fill="none" />
    <path d="M7 7v-2h10v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Products = () => {
  const { t, isRtl } = useLanguage();
  const navigate = useNavigate();
  const { workspaceUuid } = useParams();
  const [wasteModalOpen, setWasteModalOpen] = useState(false);
  const [wasteForm, setWasteForm] = useState({ type: 'waste', quantity: '', name: '', comment: '', date: new Date().toISOString().slice(0, 10), time: '' });
  const [wasteProductId, setWasteProductId] = useState(null);
  const [items, setItems] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [approvedProducts, setApprovedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportConfirm, setExportConfirm] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 720 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [form, setForm] = useState({
    product_name: '',
    product_code: '',
    batch: '',
    product_quantiry: 0,
    manufacture_date: '',
    ingredients: [] // Combined raw materials and products
  });

  // ── Group Products tab state ──────────────────────────────────────────────
  const [modalTab, setModalTab] = useState('single'); // 'single' | 'group'
  const [groupForm, setGroupForm] = useState({
    ingredients: [{ type: '', id: '', quantity: '', batch_number: '', name: '', supplier: '' }],
    products: [{ product_name: '', product_code: '', batch: '', quantity: '', manufacture_date: new Date().toISOString().slice(0, 10) }],
    manufacture_date: new Date().toISOString().slice(0, 10),
    waste_person_name: ''
  });
  const [error, setError] = useState(null);
  const [groupDeleteConfirm, setGroupDeleteConfirm] = useState({ open: false, groupId: null, count: 0, names: [] });

  const addGroupIngredient = () => setGroupForm(prev => ({
    ...prev,
    ingredients: [...prev.ingredients, { type: '', id: '', quantity: '', batch_number: '', name: '', supplier: '' }]
  }));
  const removeGroupIngredient = (idx) => setGroupForm(prev => {
    const copy = [...prev.ingredients]; copy.splice(idx, 1); return { ...prev, ingredients: copy };
  });
  const onGroupIngredientChange = (idx, field, val) => setGroupForm(prev => {
    const copy = [...prev.ingredients]; copy[idx] = { ...copy[idx], [field]: val }; return { ...prev, ingredients: copy };
  });
  const onSelectGroupIngredient = (idx, compositeId) => {
    if (!compositeId) return;
    const [type, id] = compositeId.split(':');
    if (type === 'raw') {
      const raw = rawMaterials.find(r => r._id === id);
      if (!raw) return;
      setGroupForm(prev => {
        const copy = [...prev.ingredients];
        copy[idx] = { ...copy[idx], type: 'raw', id, batch_number: raw.batch_number || '', name: raw.material_name || '', supplier: raw.supplier_name || '', available_quantity: raw.available_quantity || 0 };
        return { ...prev, ingredients: copy };
      });
    } else if (type === 'prod') {
      const prod = availableProducts.find(p => p._id === id);
      if (!prod) return;
      setGroupForm(prev => {
        const copy = [...prev.ingredients];
        copy[idx] = { ...copy[idx], type: 'prod', id, batch_number: prod.batch || '', name: prod.product_name || '', supplier: '', available_quantity: prod.product_quantiry || 0 };
        return { ...prev, ingredients: copy };
      });
    }
  };

  const addGroupProduct = () => setGroupForm(prev => ({
    ...prev,
    products: [...prev.products, { product_name: '', product_code: '', batch: '', quantity: '', manufacture_date: prev.manufacture_date }]
  }));
  const removeGroupProduct = (idx) => setGroupForm(prev => {
    const copy = [...prev.products]; copy.splice(idx, 1); return { ...prev, products: copy };
  });
  const onGroupProductChange = (idx, field, val) => setGroupForm(prev => {
    const copy = [...prev.products]; copy[idx] = { ...copy[idx], [field]: val }; return { ...prev, products: copy };
  });

  const groupTotalIngredients = groupForm.ingredients.reduce((s, u) => s + Number(u.quantity || 0), 0);
  const groupTotalOutput = groupForm.products.reduce((s, p) => s + Number(p.quantity || 0), 0);
  const groupWaste = Math.max(0, groupTotalIngredients - groupTotalOutput);

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    if (groupForm.products.length === 0) return alert(t.add_at_least_one_product);
    setSaving(true);
    try {
      const payload = {
        used_raw_materials: groupForm.ingredients.filter(i => i.type === 'raw').map(u => ({
          rawMaterialId: u.id, quantity: Number(u.quantity || 0), batch_number: u.batch_number, rawMaterialName: u.name, supplier: u.supplier
        })),
        used_products: groupForm.ingredients.filter(i => i.type === 'prod').map(u => ({
          productId: u.id, quantity: Number(u.quantity || 0)
        })),
        products: groupForm.products,
        manufacture_date: groupForm.manufacture_date,
        waste_person_name: groupForm.waste_person_name || 'System'
      };
      await axios.post(`${API_BASE}/group-products`, payload, { headers: getAuthHeaders() });
      await Promise.all([fetchItems(), fetchRawMaterials()]);
      setModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.msg || t.save_failed);
    } finally {
      setSaving(false);
    }
  };

  const handleGroupDelete = async () => {
    if (!groupDeleteConfirm.groupId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/group-products/${groupDeleteConfirm.groupId}`, { headers: getAuthHeaders() });
      await Promise.all([fetchItems(), fetchRawMaterials()]);
    } catch (err) {
      alert(err.response?.data?.msg || t.delete_failed);
    } finally {
      setDeleting(false);
      setGroupDeleteConfirm({ open: false, groupId: null, count: 0, names: [] });
    }
  };


  const apiBase = `${API_BASE}/products`;
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(apiBase, { headers: getAuthHeaders() });
      setItems(res.data || []);
      setAvailableProducts((res.data || []).filter(p => (p.product_quantiry || 0) > 0));
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  const fetchRawMaterials = async () => {
    try {
      const res = await axios.get(`${API_BASE}/rawmaterials`, { headers: getAuthHeaders() });
      setRawMaterials(res.data || []);
    } catch (err) {
      console.error('Failed to fetch raw materials', err);
    }
  };

  const fetchApprovedProducts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/approved-products`, { headers: getAuthHeaders() });
      setApprovedProducts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch approved products', err);
    }
  };

  useEffect(() => { fetchItems(); fetchRawMaterials(); fetchApprovedProducts(); }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) setMenuOpenId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const filteredItems = items.filter(it => {
    const term = searchQuery.toLowerCase();
    const statusText = (it.product_quantiry ?? it.product_quantity) > 0 ? (isRtl ? 'متوفر' : 'in stock') : (isRtl ? 'نفذ المخزون' : 'out of stock');
    const dateText = it.manufacture_date ? new Date(it.manufacture_date).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US').toLowerCase() : '';

    return term === '' ||
      it.product_name?.toLowerCase().includes(term) ||
      it.product_code?.toLowerCase().includes(term) ||
      it.batch?.toLowerCase().includes(term) ||
      statusText.includes(term) ||
      dateText.includes(term);
  });

  const downloadCSV = () => {
    const rows = filteredItems.map(it => [
      `"${it.product_name || ''}"`,
      `"${it.product_code || ''}"`,
      `"${it.batch || ''}"`,
      it.quantityProduced || it.product_quantiry || 0,
      it.product_quantiry || 0,
      `"${it.manufacture_date ? new Date(it.manufacture_date).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US') : '-'}"`,
      `"${it.product_quantiry > 0 ? (isRtl ? 'متوفر' : 'In Stock') : (isRtl ? 'نفذ المخزون' : 'Out of Stock')}"`
    ]);

    const csvContent = [
      [t.product, t.code, t.batch, t.produced, t.remaining, t.date, t.status].join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `products_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openAdd = () => {
    setIsEditing(false);
    setEditingId(null);
    setModalTab('single');
    setForm({ product_name: '', product_code: '', batch: '', product_quantiry: 0, manufacture_date: '', ingredients: [{ type: '', id: '', quantity: '', batch_number: '', name: '', supplier: '' }] });
    setGroupForm({
      ingredients: [{ type: '', id: '', quantity: '', batch_number: '', name: '', supplier: '' }],
      products: [{ product_name: '', product_code: '', batch: '', quantity: '', manufacture_date: new Date().toISOString().slice(0, 10) }],
      manufacture_date: new Date().toISOString().slice(0, 10),
      waste_person_name: ''
    });
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (it) => {
    setIsEditing(true);
    setEditingId(it._id);
    const rawIngs = (it.used_raw_materials || []).map(u => {
      const rmId = u.rawMaterialId?._id || u.rawMaterialId || '';
      const currentStock = rawMaterials.find(r => r._id === rmId);
      // Add back the quantity this product already consumed to get the "effective available"
      const effectiveAvail = (currentStock?.available_quantity || 0) + Number(u.quantity || 0);
      return {
        type: 'raw',
        id: rmId,
        quantity: u.quantity || '',
        batch_number: u.batch_number || '',
        name: u.rawMaterialId?.material_name || u.rawMaterialName || '',
        supplier: u.rawMaterialId?.supplier_name || u.supplier || '',
        available_quantity: effectiveAvail,
        isOriginal: true
      };
    });
    const prodIngs = (it.used_products || []).map(u => {
      const pId = u.productId?._id || u.productId || '';
      const currentProd = items.find(p => p._id === pId);
      const effectiveAvail = (currentProd?.product_quantiry || 0) + Number(u.quantity || 0);
      return {
        type: 'prod',
        id: pId,
        quantity: u.quantity || '',
        name: u.productId?.product_name || '',
        batch_number: u.productId?.batch || '',
        available_quantity: effectiveAvail,
        isOriginal: true
      };
    });
    const allIngs = [...rawIngs, ...prodIngs];
    setForm({
      product_name: it.product_name || '',
      product_code: it.product_code || '',
      batch: it.batch || '',
      product_quantiry: it.product_quantiry != null ? Number(it.product_quantiry) : allIngs.reduce((s, u) => s + Number(u.quantity || 0), 0),
      manufacture_date: it.manufacture_date ? new Date(it.manufacture_date).toISOString().slice(0, 10) : '',
      ingredients: allIngs
    });
    setError(null);
    setModalOpen(true);
  };

  const handleDelete = (it) => {
    setDeleteConfirm({ open: true, id: it._id, name: it.product_name });
  };

  const executeDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${apiBase}/${deleteConfirm.id}`, { headers: getAuthHeaders() });
      await Promise.all([fetchItems(), fetchRawMaterials()]);
      setDeleteConfirm({ open: false, id: null, name: '' });
    } catch (err) {
      alert(err.response?.data?.msg || t.delete_failed);
      setDeleteConfirm({ open: false, id: null, name: '' });
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = (it) => {
    if (!it.uuid) return;
    navigate(`/workspace/${workspaceUuid}/products/${it.uuid}`);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setError(null);
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const addIngredientRow = () => setForm(prev => ({ ...prev, ingredients: [...prev.ingredients, { type: '', id: '', quantity: '', batch_number: '', name: '', supplier: '' }] }));
  const removeIngredientRow = (idx) => setForm(prev => {
    const copy = [...prev.ingredients];
    copy.splice(idx, 1);
    const newTotal = calculateTotal(copy);
    return { ...prev, ingredients: copy, product_quantiry: newTotal };
  });
  const calculateTotal = (ingredients) => ingredients.reduce((s, i) => s + Number(i.quantity || 0), 0);
  const onIngredientChange = (idx, field, value) => setForm(prev => {
    const copy = [...prev.ingredients];
    copy[idx] = { ...copy[idx], [field]: value };
    const newTotal = calculateTotal(copy);
    return { ...prev, ingredients: copy, product_quantiry: newTotal };
  });

  const onSelectIngredient = (idx, compositeId) => {
    if (!compositeId) return;
    const [type, id] = compositeId.split(':');
    if (type === 'raw') {
      const raw = rawMaterials.find(r => r._id === id);
      if (!raw) return;
      setForm(prev => {
        const copy = [...prev.ingredients];
        copy[idx] = {
          ...copy[idx],
          type: 'raw',
          id: id,
          batch_number: raw.batch_number || '',
          name: raw.material_name || '',
          supplier: raw.supplier_name || raw.supplierId || '',
          available_quantity: raw.available_quantity || 0
        };
        return { ...prev, ingredients: copy };
      });
    } else if (type === 'prod') {
      const prod = availableProducts.find(p => p._id === id);
      if (!prod) return;
      setForm(prev => {
        const copy = [...prev.ingredients];
        copy[idx] = {
          ...copy[idx],
          type: 'prod',
          id: id,
          batch_number: prod.batch || '',
          name: prod.product_name || '',
          supplier: '',
          available_quantity: prod.product_quantiry || 0
        };
        return { ...prev, ingredients: copy };
      });
    }
  };

  // We remove the hard-coded useEffect for product_quantiry total to allow for manual override/deficit entry
  // But we will still provide a way to sum it if needed or just let it be manual as requested for deficit checks.

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      product_name: form.product_name,
      product_code: form.product_code,
      batch: form.batch,
      product_quantiry: Number(form.product_quantiry || 0),
      manufacture_date: form.manufacture_date,
      used_raw_materials: form.ingredients.filter(i => i.type === 'raw').map(u => ({
        rawMaterialId: u.id,
        quantity: Number(u.quantity || 0),
        batch_number: u.batch_number,
        rawMaterialName: u.name,
        supplier: u.supplier
      })),
      used_products: form.ingredients.filter(i => i.type === 'prod').map(u => ({
        productId: u.id,
        quantity: Number(u.quantity || 0)
      }))
    };
    setSaving(true);
    try {
      if (isEditing && editingId) {
        await axios.put(`${apiBase}/${editingId}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(apiBase, payload, { headers: getAuthHeaders() });
      }
      await Promise.all([fetchItems(), fetchRawMaterials()]);
      setModalOpen(false);
    } catch (err) { setError(err.response?.data?.msg || t.save_failed); }
    finally { setSaving(false); }
  };

  const openWasteModal = (productId) => {
    setWasteProductId(productId);
    setWasteForm({ type: 'waste', quantity: '', name: '', comment: '', date: new Date().toISOString().slice(0, 10), time: '' });
    setWasteModalOpen(true);
  };

  const handleWasteChange = (e) => {
    const { name, value } = e.target;
    setWasteForm(prev => ({ ...prev, [name]: value }));
  };

  const handleWasteSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/wasteproducts`, {
        productId: wasteProductId,
        type: wasteForm.type,
        quantity: Number(wasteForm.quantity),
        name: wasteForm.name,
        comment: wasteForm.comment,
        date: wasteForm.date,
        time: wasteForm.time
      }, { headers: getAuthHeaders() });

      await Promise.all([fetchItems(), fetchRawMaterials()]);
      setWasteModalOpen(false);
    } catch (err) { alert(err.response?.data?.msg || t.save_failed); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60, direction: isRtl ? 'rtl' : 'ltr' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20, flexWrap: 'wrap', gap: 16, flexDirection: isRtl ? 'row-reverse' : 'row'
      }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.products}</h1>
        <div style={{ display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
          <button onClick={() => setExportConfirm(true)} style={{
            padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2',
            background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13, cursor: 'pointer'
          }}>{t.export}</button>
          <button onClick={openAdd} style={{
            padding: '8px 16px', borderRadius: 6, border: 'none',
            background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer'
          }}>{t.add_product}</button>
        </div>
      </div>

      {/* IndexTable Card */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #ebebeb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden'
      }}>

        {/* Filter Bar */}
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          background: '#fff',
          borderBottom: '1px solid #f1f1f1',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <span style={{ position: 'absolute', [isRtl ? 'right' : 'left']: 10, top: '50%', transform: 'translateY(-50%)', color: '#8c9196' }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M19 19L14.65 14.65M17 9C17 13.4183 13.4183 17 9 17C4.58172 17 1 13.4183 1 9C1 4.58172 4.58172 1 9 1C13.4183 1 17 4.58172 17 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </span>
            <input
              placeholder={t.filter_products}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: isRtl ? '8px 34px 8px 12px' : '8px 12px 8px 34px', borderRadius: 6, border: '1px solid #d2d2d2',
                fontSize: 13, color: '#1a1c1d', outline: 'none', textAlign: isRtl ? 'right' : 'left'
              }}
            />
          </div>
        </div>

        {loading ? (
          <Loading message={t.fetching_products} />
        ) : (
          <div className="fade-in" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, direction: isRtl ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb', height: 40 }}>
                  <th style={{ width: 40 }}></th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.product}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.sku_code}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.batch_number}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.stock}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.production_date}</th>
                  <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '10px 16px', fontWeight: 600 }}>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>{t.no_products_found}</td></tr>
                ) : (
                  filteredItems.map((it) => (
                    <tr key={it._id} style={{
                      borderBottom: '1px solid #f1f1f1',
                      transition: 'background 0.2s'
                    }} onMouseEnter={(e) => e.currentTarget.style.background = '#f6f6f7'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ textAlign: 'center', padding: '12px 0' }}>
                        <button
                          onClick={() => openDetail(it)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#2c6ecb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto', padding: 4, borderRadius: 4
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#eef4ff'}
                          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          title={t.view_details}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                        </button>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{it.product_name}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.product_code}</td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.batch || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                          <span style={{ fontWeight: 700, color: '#1976d2' }}>{Number(it.product_quantiry || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</span>
                          <span style={{ color: '#6d7175' }}>/</span>
                          <span style={{ fontWeight: 600, color: '#1a1c1d' }}>{Number(it.quantityProduced ?? it.product_quantiry ?? 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</span>
                          <span style={{ color: '#6d7175', fontSize: 11, [isRtl ? 'marginRight' : 'marginLeft']: 2 }}>{t.kg}</span>
                        </div>
                        <div style={{ fontSize: 11, color: (it.product_quantiry ?? it.product_quantity) > 0 ? '#1976d2' : '#8c9196', marginTop: 2 }}>
                          {(it.product_quantiry ?? it.product_quantity) <= 0 ? t.out_of_stock : t.in_stock}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{it.manufacture_date ? new Date(it.manufacture_date).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US') : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: isRtl ? 'left' : 'right' }} onClick={(e) => e.stopPropagation()}>
                        <button className="menu-btn" onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                          setMenuOpenId(menuOpenId === it._id ? null : it._id);
                        }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 4 }}>
                          <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {modalOpen && (
        <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)', padding: isMobile ? 12 : 0 }}>
          <div className="modal-fade-in" style={{ width: 860, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{isEditing ? t.edit_product : t.add_product}</h2>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6d7175' }}>&times;</button>
            </div>

            {error && (
              <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                {error}
              </div>
            )}

            {/* Tab bar — only show on Add, not Edit */}
            {!isEditing && (
              <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #ebebeb', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                {[{ id: 'single', label: t.single_product }, { id: 'group', label: t.create_group }].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setModalTab(tab.id)}
                    style={{
                      padding: '10px 20px', border: 'none', background: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                      borderBottom: modalTab === tab.id ? '2px solid #1976d2' : '2px solid transparent',
                      color: modalTab === tab.id ? '#1976d2' : '#6d7175',
                      marginBottom: -2, transition: 'all 0.15s'
                    }}
                  >{tab.label}</button>
                ))}
              </div>
            )}


            {/* ── SINGLE PRODUCT FORM ── */}
            {(isEditing || modalTab === 'single') && (
              <form onSubmit={handleSubmit}>
                {/* Identity fields — moved to top */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
                  <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.product_name}</label>
                    <SearchableDropdown showCheckbox={false} options={approvedProducts.map(ap => ({ value: ap.name, label: ap.name }))} value={form.product_name}
                      onChange={(val) => {
                        const s = approvedProducts.find(ap => ap.name === val);
                        setError(null);
                        setForm(prev => ({ ...prev, product_name: val, product_code: s ? (s.code || s.uuid) : prev.product_code }));
                      }} placeholder={t.select_product} />
                  </div>
                  <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.product_code}</label>
                    <SearchableDropdown
                      showCheckbox={false}
                      options={approvedProducts.map(ap => ({ value: ap.code || ap.uuid, label: ap.code || ap.uuid }))}
                      value={form.product_code}
                      onChange={(val) => {
                        const s = approvedProducts.find(ap => (ap.code || ap.uuid) === val);
                        setError(null);
                        setForm(prev => ({ ...prev, product_code: val, product_name: s ? s.name : prev.product_name }));
                      }}
                      placeholder={t.select_code}
                    />
                  </div>
                  <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.batch_number}</label>
                    <input name="batch" value={form.batch} onChange={handleFormChange} placeholder={t.batch_number} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2' }} required />
                  </div>
                  <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.production_date}</label>
                    <input name="manufacture_date" type="date" value={form.manufacture_date} onChange={handleFormChange} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2' }} required />
                  </div>
                </div>

                {/* Ingredients section */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t.ingredients_materials}</h3>
                    <button type="button" onClick={addIngredientRow} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d2d2d2', background: '#fff', fontSize: 12, cursor: 'pointer' }}>+ {t.add_ingredient}</button>
                  </div>
                  {form.ingredients.map((ur, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: isMobile ? 'wrap' : 'nowrap', opacity: ur.isOriginal && isEditing ? 0.85 : 1, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                      <SearchableDropdown
                        showCheckbox={false}
                        disabled={ur.isOriginal && isEditing}
                        options={[
                          ...rawMaterials.map(r => {
                            const usedInOtherRows = form.ingredients.reduce((acc, curr, i) => {
                              if (i === idx) return acc;
                              if (curr.id === r._id && curr.type === 'raw') return acc + Number(curr.quantity || 0);
                              return acc;
                            }, 0);
                            const avail = (r.available_quantity || 0) - usedInOtherRows;
                            return { value: `raw:${r._id}`, label: `[RM] ${r.material_name} (Batch: ${r.batch_number}) - Avail: ${avail}kg` };
                          }),
                          ...availableProducts.map(p => {
                            const usedInOtherRows = form.ingredients.reduce((acc, curr, i) => {
                              if (i === idx) return acc;
                              if (curr.id === p._id && curr.type === 'prod') return acc + Number(curr.quantity || 0);
                              return acc;
                            }, 0);
                            const avail = (p.product_quantiry || 0) - usedInOtherRows;
                            return { value: `prod:${p._id}`, label: `[PR] ${p.product_name} (Batch: ${p.batch}) - Avail: ${avail} kg` };
                          })
                        ]}
                        value={ur.id ? `${ur.type}:${ur.id}` : ''}
                        onChange={(val) => onSelectIngredient(idx, val)}
                        placeholder={t.add_raw_material_subproduct}
                        style={{ flex: isMobile ? '1 1 100%' : 2 }}
                      />
                      <div style={{ position: 'relative', flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                        <input placeholder={t.quantity} type="number" step="any" value={ur.quantity}
                          disabled={ur.isOriginal && isEditing}
                          onChange={(e) => onIngredientChange(idx, 'quantity', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: 6,
                            border: `1px solid ${ur.id && !ur.isOriginal && Number(ur.quantity || 0) > (ur.available_quantity || 0) ? '#dc2626' : '#d2d2d2'}`,
                            background: ur.isOriginal && isEditing ? '#f3f4f6' : (ur.id && !ur.isOriginal && Number(ur.quantity || 0) > (ur.available_quantity || 0) ? '#fff5f5' : '#fff'),
                            cursor: ur.isOriginal && isEditing ? 'not-allowed' : 'text',
                            color: ur.isOriginal && isEditing ? '#6d7175' : '#1a1c1d',
                            textAlign: isRtl ? 'right' : 'left'
                          }}
                        />
                        {ur.isOriginal && isEditing && (
                          <div style={{ color: '#6d7175', fontSize: 10, marginTop: 2, fontStyle: 'italic' }}>{t.locked_remove_change}</div>
                        )}
                        {ur.id && !ur.isOriginal && Number(ur.quantity || 0) > (ur.available_quantity || 0) && (
                          <div style={{ color: '#dc2626', fontSize: 10, marginTop: 2, fontWeight: 600 }}>{t.insufficient_stock} ({ur.available_quantity})</div>
                        )}
                      </div>
                      <button type="button" onClick={() => removeIngredientRow(idx)} style={{ padding: '8px', background: 'none', border: '1px solid #d2d2d2', color: '#bf0711', borderRadius: 6, cursor: 'pointer' }}>&times;</button>
                    </div>
                  ))}
                </div>

                {/* Total quantity summary */}
                <div style={{ display: 'flex', alignItems: 'center', background: '#f9fafb', padding: '12px 16px', borderRadius: 8, border: '1px solid #ebebeb', marginBottom: 24, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <div style={{ flex: 1, textAlign: isRtl ? 'right' : 'left' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#1a1c1d' }}>{t.total_quantity} ({t.produced})</label>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6d7175' }}>{t.calculated_weight_msg}</p>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1976d2' }}>
                    {form.product_quantiry} {t.kg}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #ebebeb', paddingTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff' }}>{t.cancel_btn}</button>
                  <button type="submit"
                    disabled={saving || form.ingredients.some(i => i.id && Number(i.quantity || 0) > (i.available_quantity || 0))}
                    style={{
                      padding: '8px 16px', borderRadius: 6, border: 'none',
                      background: (saving || form.ingredients.some(i => i.id && Number(i.quantity || 0) > (i.available_quantity || 0))) ? '#ccc' : '#1976d2',
                      color: '#fff', fontWeight: 600, cursor: (saving || form.ingredients.some(i => i.id && Number(i.quantity || 0) > (i.available_quantity || 0))) ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 120
                    }}>
                    {saving ? <div className="dots-loading"><span></span><span></span><span></span></div> : t.save_product}
                  </button>
                </div>
              </form>
            )}

            {/* ── GROUP PRODUCTS FORM ── */}
            {!isEditing && modalTab === 'group' && (
              <form onSubmit={handleGroupSubmit}>
                {/* Products in Group — moved to top */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t.products_in_group}</h3>
                    <button type="button" onClick={addGroupProduct} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d2d2d2', background: '#fff', fontSize: 12, cursor: 'pointer' }}>+ {t.add_product}</button>
                  </div>

                  {/* Shared date */}
                  <div style={{ marginBottom: 12, textAlign: isRtl ? 'right' : 'left' }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: '#6d7175' }}>{t.mfg_date_shared}</label>
                    <input type="date" value={groupForm.manufacture_date}
                      onChange={e => setGroupForm(prev => ({ ...prev, manufacture_date: e.target.value, products: prev.products.map(p => ({ ...p, manufacture_date: e.target.value })) }))}
                      style={{ [isRtl ? 'marginRight' : 'marginLeft']: 8, padding: '5px 10px', borderRadius: 6, border: '1px solid #d2d2d2', fontSize: 13, textAlign: isRtl ? 'right' : 'left' }}
                    />
                  </div>

                  {groupForm.products.map((p, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 10, padding: '10px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #ebebeb', direction: isRtl ? 'rtl' : 'ltr' }}>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#6d7175', marginBottom: 3 }}>{t.product_name}</label>
                        <SearchableDropdown showCheckbox={false}
                          options={approvedProducts.map(ap => ({ value: ap.name, label: ap.name }))}
                          value={p.product_name}
                          onChange={(val) => { const s = approvedProducts.find(ap => ap.name === val); onGroupProductChange(idx, 'product_name', val); if (s) onGroupProductChange(idx, 'product_code', s.code || s.uuid || ''); }}
                          placeholder={t.select_product}
                        />
                      </div>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#6d7175', marginBottom: 3 }}>{t.code}</label>
                        <SearchableDropdown
                          showCheckbox={false}
                          options={approvedProducts.map(ap => ({ value: ap.code || ap.uuid, label: ap.code || ap.uuid }))}
                          value={p.product_code}
                          onChange={(val) => {
                            const s = approvedProducts.find(ap => (ap.code || ap.uuid) === val);
                            onGroupProductChange(idx, 'product_code', val);
                            if (s) onGroupProductChange(idx, 'product_name', s.name);
                          }}
                          placeholder={t.code}
                        />
                      </div>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#6d7175', marginBottom: 3 }}>{t.batch}</label>
                        <input value={p.batch} onChange={e => onGroupProductChange(idx, 'batch', e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #d2d2d2', fontSize: 13, textAlign: isRtl ? 'right' : 'left' }} />
                      </div>
                      <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <label style={{ display: 'block', fontSize: 11, color: '#6d7175', marginBottom: 3 }}>{t.product_qty}</label>
                        <input type="number" step="any" value={p.quantity} onChange={e => onGroupProductChange(idx, 'quantity', e.target.value)} style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #d2d2d2', fontSize: 13, textAlign: isRtl ? 'right' : 'left' }} required min="0" />
                      </div>
                      <button type="button" onClick={() => removeGroupProduct(idx)}
                        style={{ alignSelf: 'flex-end', padding: '8px', background: 'none', border: '1px solid #d2d2d2', color: '#bf0711', borderRadius: 6, cursor: 'pointer' }}>&times;</button>
                    </div>
                  ))}
                </div>

                {/* Shared Ingredients — moved below */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                    <div style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t.shared_ingredients}</h3>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6d7175' }}>{t.ingredient_desc}</p>
                    </div>
                    <button type="button" onClick={addGroupIngredient} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d2d2d2', background: '#fff', fontSize: 12, cursor: 'pointer' }}>+ {t.add_ingredient}</button>
                  </div>
                  {groupForm.ingredients.map((ur, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                      <SearchableDropdown showCheckbox={false}
                        options={[
                          ...rawMaterials.map(r => {
                            const usedInOtherRows = groupForm.ingredients.reduce((acc, curr, i) => {
                              if (i === idx) return acc;
                              if (curr.id === r._id && curr.type === 'raw') return acc + Number(curr.quantity || 0);
                              return acc;
                            }, 0);
                            const avail = (r.available_quantity || 0) - usedInOtherRows;
                            return { value: `raw:${r._id}`, label: `[RM] ${r.material_name} (Batch: ${r.batch_number}) - Avail: ${avail}kg` };
                          }),
                          ...availableProducts.map(p => {
                            const usedInOtherRows = groupForm.ingredients.reduce((acc, curr, i) => {
                              if (i === idx) return acc;
                              if (curr.id === p._id && curr.type === 'prod') return acc + Number(curr.quantity || 0);
                              return acc;
                            }, 0);
                            const avail = (p.product_quantiry || 0) - usedInOtherRows;
                            return { value: `prod:${p._id}`, label: `[PR] ${p.product_name} (Batch: ${p.batch}) - Avail: ${avail} kg` };
                          })
                        ]}
                        value={ur.id ? `${ur.type}:${ur.id}` : ''}
                        onChange={(val) => onSelectGroupIngredient(idx, val)}
                        placeholder={t.add_raw_material_subproduct}
                        style={{ flex: isMobile ? '1 1 100%' : 2 }}
                      />
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input placeholder={t.quantity} type="number" step="any" value={ur.quantity}
                          onChange={(e) => onGroupIngredientChange(idx, 'quantity', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: 6,
                            border: `1px solid ${ur.id && Number(ur.quantity || 0) > (ur.available_quantity || 0) ? '#dc2626' : '#d2d2d2'}`,
                            background: ur.id && Number(ur.quantity || 0) > (ur.available_quantity || 0) ? '#fff5f5' : '#fff',
                            textAlign: isRtl ? 'right' : 'left'
                          }}
                        />
                        {ur.id && Number(ur.quantity || 0) > (ur.available_quantity || 0) && (
                          <div style={{ color: '#dc2626', fontSize: 10, marginTop: 2, fontWeight: 600 }}>{t.insufficient_stock} ({ur.available_quantity})</div>
                        )}
                      </div>
                      <button type="button" onClick={() => removeGroupIngredient(idx)} style={{ padding: '8px', background: 'none', border: '1px solid #d2d2d2', color: '#bf0711', borderRadius: 6, cursor: 'pointer' }}>&times;</button>
                    </div>
                  ))}
                </div>

                {/* Summary bar */}
                <div style={{
                  display: 'flex', gap: 16, padding: '12px 16px',
                  background: groupTotalOutput > groupTotalIngredients ? '#fef2f2' : '#e3f2fd',
                  borderRadius: 8, border: `1px solid ${groupTotalOutput > groupTotalIngredients ? '#fecaca' : '#90caf9'}`,
                  marginBottom: 20, fontSize: 13, transition: 'all 0.2s',
                  flexDirection: isRtl ? 'row-reverse' : 'row'
                }}>
                  <span><b>{t.ingredients_materials}:</b> {groupTotalIngredients.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}</span>
                  <span style={{ color: groupTotalOutput > groupTotalIngredients ? '#dc2626' : 'inherit', fontWeight: groupTotalOutput > groupTotalIngredients ? 700 : 400 }}>
                    <b>{t.total} {t.produced}:</b> {groupTotalOutput.toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })}
                  </span>
                  <span style={{ color: groupWaste > 0 ? (groupTotalOutput > groupTotalIngredients ? '#dc2626' : '#d97706') : '#6d7175' }}>
                    <b>{groupTotalOutput > groupTotalIngredients ? t.deficit : t.waste}:</b> {groupTotalOutput > groupTotalIngredients ? (groupTotalOutput - groupTotalIngredients).toFixed(2) : groupWaste.toFixed(2)}
                  </span>
                  <span><b>{t.products}:</b> {groupForm.products.length}</span>
                </div>
                {groupTotalOutput > groupTotalIngredients && (
                  <div style={{ marginBottom: 16, color: '#dc2626', fontSize: 12, fontWeight: 600, [isRtl ? 'paddingRight' : 'paddingLeft']: 4, textAlign: isRtl ? 'right' : 'left' }}>
                    ⚠️ {t.output_exceeds || "Output exceeds ingredients total!"}
                  </div>
                )}

                {/* Waste person name */}
                {groupWaste > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4, textAlign: isRtl ? 'right' : 'left' }}>{t.waste_person_name}</label>
                    <input value={groupForm.waste_person_name} onChange={e => setGroupForm(prev => ({ ...prev, waste_person_name: e.target.value }))}
                      placeholder={t.full_name} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', fontSize: 13, textAlign: isRtl ? 'right' : 'left' }} />
                  </div>
                )}

                <div style={{ borderTop: '1px solid #ebebeb', paddingTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <button type="button" onClick={() => setModalOpen(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff' }}>{t.cancel_btn}</button>
                  <button type="submit" disabled={saving || groupForm.products.length === 0 || groupTotalOutput > groupTotalIngredients || groupForm.ingredients.some(i => i.id && Number(i.quantity || 0) > (i.available_quantity || 0))}
                    style={{
                      padding: '8px 20px', borderRadius: 6, border: 'none',
                      background: (saving || groupForm.products.length === 0 || groupTotalOutput > groupTotalIngredients || groupForm.ingredients.some(i => i.id && Number(i.quantity || 0) > (i.available_quantity || 0))) ? '#ccc' : '#1976d2',
                      color: '#fff', fontWeight: 600, cursor: (saving || groupForm.products.length === 0 || groupTotalOutput > groupTotalIngredients || groupForm.ingredients.some(i => i.id && Number(i.quantity || 0) > (i.available_quantity || 0))) ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 160
                    }}>
                    {saving ? <div className="dots-loading"><span></span><span></span><span></span></div> : `${t.create} ${groupForm.products.length} ${t.products}`}
                  </button>
                </div>
              </form>
            )}

          </div>
        </div>
      )}


      {/* Waste Modal */}
      {
        wasteModalOpen && (
          <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(2px)' }}>
            <div className="modal-fade-in" style={{ width: 480, background: '#fff', borderRadius: 12, padding: 24, direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700 }}>{t.waste_form_title}</h2>
              <form onSubmit={handleWasteSubmit}>
                <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.type}</label>
                    <SearchableDropdown
                      showCheckbox={false}
                      options={[
                        { value: 'waste', label: t.waste },
                        { value: 'tested', label: t.tested }
                      ]}
                      value={wasteForm.type}
                      onChange={(val) => setWasteForm(prev => ({ ...prev, type: val }))}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.quantity}</label>
                    <input name="quantity" type="number" value={wasteForm.quantity} onChange={handleWasteChange} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} required />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.full_name}</label>
                    <input name="name" value={wasteForm.name} onChange={handleWasteChange} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} required />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.date}</label>
                      <input name="date" type="date" value={wasteForm.date} onChange={handleWasteChange} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} required />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.time}</label>
                      <input name="time" type="time" value={wasteForm.time} onChange={handleWasteChange} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', textAlign: isRtl ? 'right' : 'left' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t.comment} ({t.optional})</label>
                    <textarea name="comment" value={wasteForm.comment} onChange={handleWasteChange} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d2d2d2', minHeight: 80, resize: 'vertical', textAlign: isRtl ? 'right' : 'left' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  <button type="button" onClick={() => setWasteModalOpen(false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff' }}>{t.cancel_btn}</button>
                  <button type="submit" disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 80 }}>
                    {saving ? <div className="dots-loading"><span></span><span></span><span></span></div> : t.save}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Floating Menu */}
      {
        menuOpenId && (
          <div className="floating-menu" style={{
            position: 'fixed',
            [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 120),
            top: menuPos.y, background: '#fff',
            borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
            border: '1px solid #ebebeb'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', flexDirection: 'column', padding: 4 }}>
              <button onClick={() => { const it = items.find(i => i._id === menuOpenId); setMenuOpenId(null); if (it) openEdit(it); }} style={{
                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4
              }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.edit}</button>
              <button onClick={() => { const id = menuOpenId; setMenuOpenId(null); openWasteModal(id); }} style={{
                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                cursor: 'pointer', color: '#a9712a', fontSize: 13, borderRadius: 4
              }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.record_waste}</button>
              <button onClick={() => {
                const it = items.find(i => i._id === menuOpenId);
                setMenuOpenId(null);
                if (!it) return;
                if (it.groupId) {
                  // Group product — delete all in group
                  const siblings = items.filter(i => i.groupId && String(i.groupId) === String(it.groupId));
                  setGroupDeleteConfirm({ open: true, groupId: it.groupId, count: siblings.length, names: siblings.map(s => s.product_name) });
                } else {
                  handleDelete(it);
                }
              }} style={{
                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4
              }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>{t.delete}</button>
            </div>
          </div>
        )
      }
      {
        deleteConfirm.open && (
          <DeleteConfirmation
            itemName={deleteConfirm.name}
            onConfirm={executeDelete}
            onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
            isDeleting={deleting}
          />
        )
      }
      {
        groupDeleteConfirm.open && (
          <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26,28,29,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1500, backdropFilter: 'blur(3px)' }}>
            <div className="modal-fade-in" style={{ width: 440, background: '#fff', borderRadius: 12, padding: 28, boxShadow: '0 20px 40px rgba(0,0,0,0.2)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <div style={{ width: 40, height: 40, background: '#fff1f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>⚠️</div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#d82c0d' }}>{t.delete_group} ({groupDeleteConfirm.count} {t.products})</h2>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
                {t.delete_group_warning.replace('{count}', groupDeleteConfirm.count)}
              </p>
              <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#374151' }}>
                {groupDeleteConfirm.names.map((n, i) => <div key={i} style={{ marginBottom: 2 }}>• {n}</div>)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <button onClick={() => setGroupDeleteConfirm({ open: false, groupId: null, count: 0, names: [] })} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff', cursor: 'pointer' }}>{t.cancel_btn}</button>
                <button onClick={handleGroupDelete} disabled={deleting}
                  style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#d82c0d', color: '#fff', fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                  {deleting ? <div className="dots-loading"><span></span><span></span><span></span></div> : `${t.delete} ${t.all} ${groupDeleteConfirm.count}`}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        exportConfirm && (
          <ExportConfirmation
            itemName={t.products}
            onConfirm={() => { downloadCSV(); setExportConfirm(false); }}
            onCancel={() => setExportConfirm(false)}
          />
        )
      }
    </div >
  );
};

export default Products;
